import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expiredBilling, setExpiredBilling] = useState(null);

  const checkAuth = useCallback(async () => {
    // Solo cerramos sesión ante un 401/403 real. Ante errores de red/5xx/timeout
    // reintentamos para NO botar al usuario al recargar la página.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setExpiredBilling(null);
        setLoading(false);
        return;
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 403 && detail === "LICENSE_EXPIRED") {
          // Licencia vencida: NO cerramos sesión; mostramos la pantalla de renovación.
          try {
            const { data: bs } = await api.get("/auth/billing-status");
            setExpiredBilling(bs);
          } catch (e) { setExpiredBilling({ expired: true }); }
          setUser(null);
          setLoading(false);
          return;
        }
        if (status === 401 || status === 403) {
          setUser(null);
          setExpiredBilling(null);
          setLoading(false);
          return;
        }
        // error transitorio: espera breve y reintenta
        await new Promise((r) => setTimeout(r, 600));
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(null);
    setExpiredBilling(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, expiredBilling }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
